import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';
import { Button, Empty, Menu, Skeleton } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import { memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { CollapsedSidebarNumbersView } from './collapsed-sidebar-numbers.view';
import { useLayoutContext } from '@/container/context/layout/layout-context';
import { SidebarNumberProps } from '@modules/channels/channel.types';
import { ChannelCard } from './card-assign-channel';
import { RiAddLine, RiArrowDownSLine } from 'react-icons/ri';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import { ROLE_MAPS } from '@shared/constants/roles';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { FaChevronRight } from 'react-icons/fa6';
import { AccessControlledWrapper } from '@components/plan-role-access-control-wrapper/access-controlled-wrapper';

const className: MenuItemType['className'] = 'group pl-1.5! pr-0! mx-0! h-10! lg:h-11! w-full! overflow-visible!';

function SidebarNumbers(props: SidebarNumberProps) {
  const { isMobile } = useScreenSize();
  const {
    assignChannels: { data, initialLoading, hasNextPage },
    layoutContext,
  } = props;
  const { closeSidebarMobileDrawer, sidebarCollapsed } = layoutContext;
  const navigate = useNavigate();
  const { channelId: queryChannelId = '' } = useParams();
  const [, , objToEncryptedParams] = useEncryptedParams();
  const { isSubmenuOverlay, setIsSubmenuOverlay } = useLayoutContext();

  const numbers = data?.data ? [...data.data] : [];

  const [canPurchase] = hasRoleAccess(ROLE_MAPS.SETTINGS_MY_NUMBERS_PURCHASE_NUMBER);
  const { t } = useTranslation();

  const handleViewAll = () => {
    if (isMobile) {
      navigate('/settings/my-numbers');
      closeSidebarMobileDrawer();
      return;
    }

    if (isSubmenuOverlay === 'channels') {
      setIsSubmenuOverlay(null);
    } else {
      setIsSubmenuOverlay('channels');
    }
  };

  if (initialLoading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton active paragraph={false} />
        <Skeleton active paragraph={false} />
      </div>
    );
  }

  return (
    <>
      <div className={clsx(sidebarCollapsed ? 'hidden' : '')}>
        <Menu
          selectedKeys={[String(queryChannelId)]}
          inlineCollapsed={false}
          defaultOpenKeys={['numbers']}
          mode="inline"
          className="custom-sidebar-menu px-2.5! [&_.ant-menu-title-content]:order-1! [&_.ant-menu-submenu-title]:px-0.5!"
          items={[
            {
              key: 'numbers',
              label: (
                <div className="text-text-600 flex justify-between items-center ml-0.5">
                  <p className="uppercase leading-4! text-xs tracking-wider">{t('number', { count: 5 })}</p>
                  <AccessControlledWrapper hasRoleAccess={canPurchase} tooltip={capitalize(t('purchase_number'))}>
                    <Button
                      type="text"
                      size="small"
                      disabled={!canPurchase}
                      className="p-0! min-w-0! h-auto! leading-none! text-text-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/settings/my-numbers/purchase-number');
                        closeSidebarMobileDrawer();
                      }}
                    >
                      <RiAddLine className="size-5" />
                    </Button>
                  </AccessControlledWrapper>
                </div>
              ),
              expandIcon: ({ isOpen }) => (
                <RiArrowDownSLine size={20} className={`transition-transform ${isOpen ? 'rotate-0' : 'rotate-180'}`} />
              ),
              className: clsx(
                'mx-auto! [&_.ant-menu-submenu-title]:hover:bg-gray-200! [&_.ant-menu-submenu-title]:w-full!',
                '[&_.ant-menu-submenu-title]:my-0! [&_.ant-menu-submenu-title]:mx-0! [&_.ant-menu-submenu-title]:px-0.5!'
              ),
              children:
                numbers.length > 0
                  ? numbers
                      ?.map((number) => ({
                        key: number?.channel?.id + '',
                        label: <ChannelCard {...number} />,
                        channelId: number?.channel?.id,
                        channelName: number?.channel?.friendlyName + '',
                      }))
                      .map(({ channelId, channelName, ...item }) => ({
                        ...item,
                        className,
                        onClick: () => {
                          closeSidebarMobileDrawer();

                          navigate({
                            pathname: '/conversations/' + channelId,
                            search: objToEncryptedParams({
                              channelName,
                            }),
                          });
                        },
                      }))
                  : [
                      {
                        key: 'empty',
                        className: 'h-auto! p-0!',
                        label: (
                          <Empty
                            className="justify-self-start! mx-auto!"
                            description={capitalize(t('no_numbers_found'))}
                          />
                        ),
                      },
                    ],
            },
          ]}
        />
        {hasNextPage && (
          <div
            id="view-all"
            className={clsx(
              'flex items-center ml-3 mt-1.5 gap-1 cursor-pointer w-fit py-1 px-2 rounded-full hover:text-primary-400',
              isSubmenuOverlay === 'channels' ? 'text-primary-600 bg-primary-50' : 'text-text-600',
              hasNextPage ? '' : 'invisible'
            )}
            onClick={handleViewAll}
          >
            <p className="inline cursor-pointer text-xs tracking-wider rounded-xl uppercase!"> {t('view_all')}</p>
            <FaChevronRight size={10} style={{ transform: isSubmenuOverlay === 'channels' ? 'rotate(180deg)' : '' }} />
          </div>
        )}
      </div>
      <div className={clsx(sidebarCollapsed ? '' : 'hidden')}>
        <CollapsedSidebarNumbersView {...props} />
      </div>
    </>
  );
}

export const SidebarNumbersView = memo(SidebarNumbers);
